import { Button } from '@/components/UI'
import { Dialog } from '@/components/UI/Dialog'
import { HtmlContent } from '@/components/UI/HtmlContent'
import { zodResolver } from '@hookform/resolvers/zod'
import { memo, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { KeywordCharInput, isCharInputLength, matchesAnswerLength } from './KeywordCharInput'

// ---------------------------------------------------------------------------
// Schema & Types
// ---------------------------------------------------------------------------

/**
 * answerLength が指定されたときは文字数が一致するまで送信できないようにする（#2591）。
 * 未指定（従来のテキストボックス）は 1 文字以上で送信可
 */
const buildKeywordSchema = (answerLength: number | null) =>
  z.object({
    keyword: answerLength === null ? z.string().min(1) : z.string().refine((v) => matchesAnswerLength(v, answerLength)),
  })
type KeywordFormData = z.infer<ReturnType<typeof buildKeywordSchema>>

type Props = {
  onClose: () => void
  /** storybookで確認する際に、スクロール可にするためのフラグ。raundで利用する場合にはtrue */
  scrollLock?: boolean
  /** 管理画面で登録したクイズタイトル。未指定時は i18n フォールバック「クイズ」を使用 */
  title?: string | null
  /** クイズの問題文 */
  question?: string
  /** クイズ画像URL */
  image?: string | null
  /** ヒントテキスト */
  hint?: string | null
  /** 回答ボタンを押したときのコールバック */
  onSubmit: (keyword: string) => void
  /** API送信中のローディング状態 */
  isLoading?: boolean
  /**
   * 正解の文字数（コードポイント単位）。1 以上 MAX_CHAR_INPUT_LENGTH 以下のとき、文字数ぶんの1文字入力欄にする（#2591）。
   * null / undefined / 0 以下 / 上限超えのときは従来のテキストボックス。
   */
  answerLength?: number | null
}

/**
 * `Raund-Replace / Screen UI / SPOT-DETAIL-スタンプ獲得（クイズ・キーワード入力）`のダイアログコンポーネント
 * Figma node: 439:11027
 */
export const DialogQuizKeyword = memo(
  ({ onClose, scrollLock = false, title, question, image, hint, onSubmit, isLoading = false, answerLength }: Props) => {
    const { t } = useTranslation()
    // 1 以上・上限以下の整数のときだけ1文字入力を使う
    const charCount = isCharInputLength(answerLength) ? answerLength : null
    // zodResolver ごとメモ化する（schema だけメモ化してもレンダーごとに resolver が作り直される）
    const resolver = useMemo(() => zodResolver(buildKeywordSchema(charCount)), [charCount])
    const {
      register,
      control,
      handleSubmit,
      formState: { isValid },
    } = useForm<KeywordFormData>({
      resolver,
      mode: 'onChange',
      defaultValues: { keyword: '' },
    })

    const onFormSubmit = handleSubmit(({ keyword }) => {
      onSubmit(keyword)
    })

    return (
      <Dialog
        selectorState={true}
        compact
        showCloseButton={true}
        closeButtonInline
        lockScroll={scrollLock}
        closeAction={onClose}
        title={title ?? t('spot_detail.quiz.title', 'クイズ')}
      >
        <div data-testid="dialog-quiz-keyword">
          {image && (
            <div className="mb-[15px] overflow-hidden">
              <img src={image} alt="" className="w-full h-[168px] object-cover" />
            </div>
          )}

          {question && <HtmlContent html={question} variant="subtitle1" className="mb-[15px] font-bold text-black" />}

          <form onSubmit={onFormSubmit} className="flex flex-col gap-[10px]">
            {charCount !== null ? (
              <Controller
                control={control}
                name="keyword"
                render={({ field }) => (
                  <KeywordCharInput
                    inputRef={field.ref}
                    length={charCount}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={isLoading}
                  />
                )}
              />
            ) : (
              <input
                {...register('keyword')}
                type="text"
                placeholder={t('spot_detail.quiz.keyword_placeholder', 'キーワードを入力')}
                className="w-full h-[36px] px-[12px] bg-white border border-[#757575] rounded-[8px] text-base text-black outline-none focus:border-main"
              />
            )}

            {hint && <p className="text-[12px] text-[#757575] leading-[16px]">{hint}</p>}

            <Button
              label={t('spot_detail.quiz.submit', '回答する')}
              variant="primary"
              size="sm"
              fullWidth
              type="submit"
              disabled={!isValid || isLoading}
              loading={isLoading}
            />
          </form>
        </div>
      </Dialog>
    )
  },
)
